import {
  computeStatBudget,
  computeStats,
  computeWeaponStats,
  validateVehicleBlueprint,
  LIMITS,
  CAR_PRESETS,
  DEFAULT_UPGRADES,
  DEFAULT_WEAPON_UPGRADES,
  type CarUpgrade,
  type WeaponUpgrade,
  type VehicleBlueprint,
  type GameMode,
  type RenderMode,
} from "@neon-arena/shared";

import { BindingsPanel } from "./BindingsPanel.js";
import { VehicleShowcase } from "./VehicleShowcase.js";
import { AIBuilderModal } from "./AIBuilderModal.js";
import { isTouchDevice } from "../input/TouchInput.js";
import { buildLeaderboardContentHtml } from "./leaderboardView.js";
import type { AuthClient, LeaderboardEntry } from "../auth/AuthClient.js";

import balanced from "../../../content/vehicles/balanced-starter.json" with { type: "json" };
import scout from "../../../content/vehicles/glass-cannon-scout.json" with { type: "json" };
import bruiser from "../../../content/vehicles/heavy-bruiser.json" with { type: "json" };
import striker from "../../../content/vehicles/long-range-striker.json" with { type: "json" };
import drifter from "../../../content/vehicles/drift-interceptor.json" with { type: "json" };

const RAW: unknown[] = [balanced, scout, bruiser, striker, drifter];

export type ControlMethod = "keyboard" | "controller" | "touch";

/** What the lobby hands to its host when the player confirms a loadout. */
export interface VehicleSelectResult {
  driverName: string;
  serverUrl: string;
  blueprint: VehicleBlueprint;
  keyboardOnly: boolean;
  controlMethod: ControlMethod;
  upgrades: CarUpgrade;
  weaponUpgrades: WeaponUpgrade;
  dummyOpponent: boolean;
  gameMode: GameMode;
  preferredTeam: number;
  renderMode: RenderMode;
}

export interface VehicleSelectOptions {
  onConfirm: (result: VehicleSelectResult) => void;
  /**
   * QUICK MATCH — same loadout the player already has selected, straight into
   * the queue. Deliberately reuses this screen's state instead of asking
   * anything: name and vehicle are whatever the lobby is already showing
   * (docs/menu-redesign-plan.md §3.4, "Quick Match asks nothing").
   * `gameMode` / `preferredTeam` in the result are ignored by the queue — the
   * queue server owns the match type.
   */
  onQuickMatch?: (result: VehicleSelectResult) => void;
  auth?: AuthClient | null;
}

const UPGRADE_KEYS: Array<keyof CarUpgrade> = ["engine", "gearbox", "turbo", "suspension", "aero", "brakes", "boost"];

export class VehicleSelect {
  private root = document.getElementById("start-screen")!;
  private joinBtn = document.getElementById("join-btn") as HTMLButtonElement;
  private nameInput = document.getElementById("name-input") as HTMLInputElement;
  private serverInput = document.getElementById("server-input") as HTMLInputElement;
  private vehicles: VehicleBlueprint[] = [];
  private selectedIdx = 0;
  private selectedPreset = "stock";
  private currentUpgrades: CarUpgrade = { ...DEFAULT_UPGRADES };
  private currentWeaponUpgrades: WeaponUpgrade = { ...DEFAULT_WEAPON_UPGRADES };
  private controlMethod: ControlMethod = isTouchDevice() ? "touch" : "keyboard";
  private gamepadPromptEl: HTMLElement | null = null;
  private gamepadPollId = 0;
  private prevPadBtns: boolean[] = [];
  private gpTestPollId = 0;
  private gamepadConfirmed = false;
  private gameMode: GameMode = "lms";
  private preferredTeam = 0;
  private renderMode: RenderMode = "solid";
  private presetSelector = document.getElementById("preset-selector")!;
  private presetStatPreview = document.getElementById("preset-stat-preview")!;
  private bindingsPanel: BindingsPanel;
  private activeTab = "play";
  private auth: AuthClient | null = null;
  private showcase: VehicleShowcase | null = null;
  private aiModal: AIBuilderModal | null = null;
  private customVehicleIds = new Set<string>();

  // ── M3 arcade-screen delegation state ─────────────────────────────────────
  // The GARAGE / OPTIONS screens re-house this screen's DOM and drive its
  // logic through the small facade below; these flags let the existing rAF
  // loops keep running while #start-screen itself stays hidden. All default
  // to inert so the classic (?ui=classic) flow is byte-identical.
  /** Gamepad-test loop liveness (was keyed off activeTab === "controls"). */
  private gpTestActive = false;
  /** True while the OPTIONS screen hosts the control-method row. */
  private embeddedActive = false;
  /** True while the GARAGE screen hosts the showcase + upgrade panels. */
  private garageActive = false;
  /** Guest pressed a sign-in-gated control. Default: classic ACCOUNT tab. */
  private requireSignIn: () => void = () => this.switchTab("account");
  /** Fires when the vehicle roster or selection changes (GARAGE list sync). */
  private vehiclesChanged: (() => void) | null = null;

  constructor(private opts: VehicleSelectOptions) {
    this.auth = opts.auth ?? null;
    this.loadVehicles();
    this.setDefaultServerUrl();
    this.joinBtn.addEventListener("click", () => this.confirm());
    window.addEventListener("keydown", (e) => {
      if (!this.isVisible()) return;
      if (e.code === "Enter") this.confirm();
    });
    this.bindControlMethodBtns();
    window.addEventListener("gamepadconnected", () => {
      if (!this.isVisible()) return;
      if (this.controlMethod !== "controller") {
        this.controlMethod = "controller";
        this.syncControlBtns();
      }
      this.gamepadConfirmed = true;
      this.setJoinBlocked(false);
      this.dismissGamepadPrompt(true);
    });
    this.bindModeBtns();
    this.bindTeamBtns();
    this.bindVisBtns();
    this.bindingsPanel = new BindingsPanel(document.getElementById("bindings-inline")!);
    this.bindTabs();
    this.renderActiveVehicleSummary();

    // 3D vehicle showcase
    const previewCanvas = document.getElementById("vehicle-preview-canvas") as HTMLCanvasElement | null;
    if (previewCanvas && this.vehicles.length > 0) {
      this.showcase = new VehicleShowcase(previewCanvas, this.vehicles, (idx) => {
        this.selectedIdx = idx;
        this.renderVehicleInfo();
        this.renderActiveVehicleSummary();
        this.vehiclesChanged?.();
      });
    }
    this.wireVehicleNav();

    // AI builder modal — instantiate once, open on button click
    if (document.getElementById("ai-builder-modal")) {
      this.aiModal = new AIBuilderModal(
        this.auth,
        () => this.serverInput.value || "ws://localhost:2567",
        () => { void this.loadCustomVehicles(); },
      );
      document.getElementById("ai-builder-btn")?.addEventListener("click", () => {
        if (!this.auth?.isLoggedIn) {
          this.requireSignIn();
          return;
        }
        this.aiModal?.open();
      });
    }

    // Edit style button for custom vehicles
    document.getElementById("vehicle-edit-style-btn")?.addEventListener("click", () => {
      const bp = this.vehicles[this.selectedIdx];
      if (bp && this.customVehicleIds.has(bp.id) && this.auth?.isLoggedIn) {
        this.aiModal?.openEdit(bp);
      }
    });

    // Delete button for custom vehicles
    document.getElementById("vehicle-delete-btn")?.addEventListener("click", () => {
      const bp = this.vehicles[this.selectedIdx];
      if (bp && this.customVehicleIds.has(bp.id)) {
        void this.removeCustomVehicle(bp.id);
      }
    });

    // Load custom vehicles if already logged in at startup
    if (this.auth?.isLoggedIn) void this.loadCustomVehicles();
  }

  private setDefaultServerUrl(): void {
    const { hostname, port, protocol } = window.location;
    if (hostname === "localhost" || hostname === "127.0.0.1") return;
    const wsProto = protocol === "https:" ? "wss:" : "ws:";
    const isStandardPort = !port || port === "80" || port === "443";
    this.serverInput.value = isStandardPort
      ? `${wsProto}//${hostname}`
      : `${wsProto}//${hostname}:2567`;
  }

  private bindModeBtns(): void {
    const btns = document.querySelectorAll<HTMLButtonElement>(".mode-btn");
    btns.forEach((btn) => {
      btn.addEventListener("click", () => {
        this.gameMode = btn.dataset.mode as GameMode;
        btns.forEach((b) => b.classList.toggle("selected", b === btn));
        const wrap = document.getElementById("team-selector-wrap");
        if (wrap) wrap.classList.toggle("hidden", this.gameMode !== "team-battle");
      });
    });
    btns.forEach((b) => b.classList.toggle("selected", b.dataset.mode === this.gameMode));
  }

  private bindTeamBtns(): void {
    const btns = document.querySelectorAll<HTMLButtonElement>(".team-btn");
    btns.forEach((btn) => {
      btn.addEventListener("click", () => {
        this.preferredTeam = parseInt(btn.dataset.team ?? "0");
        btns.forEach((b) => b.classList.toggle("selected", b === btn));
      });
    });
    btns.forEach((b) => b.classList.toggle("selected", parseInt(b.dataset.team ?? "0") === this.preferredTeam));
  }

  private bindVisBtns(): void {
    const btns = document.querySelectorAll<HTMLButtonElement>(".vis-btn");
    btns.forEach((btn) => {
      btn.addEventListener("click", () => {
        this.renderMode = btn.dataset.vis as RenderMode;
        btns.forEach((b) => b.classList.toggle("selected", b === btn));
      });
    });
    btns.forEach((b) => b.classList.toggle("selected", b.dataset.vis === this.renderMode));
  }

  private syncControlBtns(): void {
    document.querySelectorAll<HTMLButtonElement>(".ctrl-btn").forEach((b) => {
      b.classList.toggle("selected", b.dataset.ctrl === this.controlMethod);
    });
  }

  private bindControlMethodBtns(): void {
    document.querySelectorAll<HTMLButtonElement>(".ctrl-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        this.controlMethod = btn.dataset.ctrl as ControlMethod;
        this.syncControlBtns();
        if (this.controlMethod === "controller") {
          this.gamepadConfirmed = false;
          this.prevPadBtns = [];
          this.setJoinBlocked(true, "🎮 Press any button on your controller to continue");
          this.startGamepadWatcher();
        } else {
          this.gamepadConfirmed = false;
          this.setJoinBlocked(false);
          this.dismissGamepadPrompt(true);
        }
      });
    });
    this.syncControlBtns();
    this.startGamepadWatcher();
  }

  private setJoinBlocked(blocked: boolean, msg = ""): void {
    this.joinBtn.disabled = blocked;
    this.showGamepadPrompt(msg || "");
    if (!blocked) this.dismissGamepadPrompt(false);
  }

  private showGamepadPrompt(msg: string): void {
    if (!msg) { this.dismissGamepadPrompt(false); return; }
    if (this.gamepadPromptEl) { this.gamepadPromptEl.textContent = msg; return; }
    const el = document.createElement("div");
    el.id = "gamepad-prompt";
    el.textContent = msg;
    document.getElementById("lobby-footer")?.prepend(el);
    this.gamepadPromptEl = el;
  }

  private dismissGamepadPrompt(stopWatcher: boolean): void {
    this.gamepadPromptEl?.remove();
    this.gamepadPromptEl = null;
    if (stopWatcher) {
      cancelAnimationFrame(this.gamepadPollId);
      this.gamepadPollId = 0;
    }
  }

  private startGamepadWatcher(): void {
    if (isTouchDevice()) return;
    if (this.gamepadPollId !== 0) return;

    const poll = (): void => {
      // Keep polling while the OPTIONS screen embeds the control-method row,
      // even though #start-screen itself is hidden (M3).
      if (!this.isVisible() && !this.embeddedActive) { this.gamepadPollId = 0; return; }
      if (typeof navigator === "undefined" || !navigator.getGamepads) {
        this.gamepadPollId = requestAnimationFrame(poll);
        return;
      }

      const pads = navigator.getGamepads();
      let activePad: Gamepad | null = null;
      for (const p of pads) { if (p?.connected) { activePad = p; break; } }

      if (!activePad) {
        this.prevPadBtns = [];
        if (this.controlMethod === "controller" && !this.gamepadConfirmed) {
          this.showGamepadPrompt("⚠️ No controller detected — connect it and press a button");
          this.setJoinBlocked(true, "⚠️ No controller detected — connect it and press a button");
        }
        this.gamepadPollId = requestAnimationFrame(poll);
        return;
      }

      // Gamepad visible in browser — show press-button prompt if not yet confirmed
      if (this.controlMethod !== "controller") {
        this.showGamepadPrompt("🎮 Controller detected — press any button to use it");
      } else if (!this.gamepadConfirmed) {
        this.setJoinBlocked(true, "🎮 Press any button on your controller to continue");
      }

      const curBtns = activePad.buttons.map((b) => b.pressed);

      // Rising-edge detection — seed prevPadBtns on first sighting to avoid phantom press
      if (this.prevPadBtns.length === 0) {
        this.prevPadBtns = curBtns;
      } else {
        const anyRisingEdge = curBtns.some((pressed, i) => pressed && !this.prevPadBtns[i]);
        if (anyRisingEdge) {
          if (this.controlMethod !== "controller") {
            this.controlMethod = "controller";
            this.syncControlBtns();
          }
          this.gamepadConfirmed = true;
          this.setJoinBlocked(false);
          this.dismissGamepadPrompt(true);
          return;
        }
        this.prevPadBtns = curBtns;
      }

      this.gamepadPollId = requestAnimationFrame(poll);
    };

    this.gamepadPollId = requestAnimationFrame(poll);
  }

  private bindTabs(): void {
    document.querySelectorAll<HTMLButtonElement>(".tab-btn").forEach((btn) => {
      btn.addEventListener("click", () => this.switchTab(btn.dataset.tab!));
    });
    document.getElementById("lb-refresh-btn")?.addEventListener("click", () => {
      void this.loadLeaderboard();
    });
  }

  switchTab(tabName: string): void {
    this.activeTab = tabName;
    document.querySelectorAll<HTMLButtonElement>(".tab-btn").forEach((btn) => {
      btn.classList.toggle("active", btn.dataset.tab === tabName);
    });
    document.querySelectorAll<HTMLElement>(".tab-pane").forEach((pane) => {
      pane.classList.toggle("hidden", pane.dataset.tab !== tabName);
    });
    if (tabName === "play") this.renderActiveVehicleSummary();
    if (tabName === "vehicle") {
      this.renderVehicleInfo();
      this.showcase?.start();
    } else {
      this.showcase?.stop();
    }
    if (tabName === "leaderboard") void this.loadLeaderboard();
    if (tabName === "upgrades") void this.openUpgradesTab();
    if (tabName === "controls") this.startGpTest();
    else this.stopGpTest();
  }

  private async openUpgradesTab(): Promise<void> {
    // Refresh credits/owned upgrades from server, then re-render
    if (this.auth?.isLoggedIn) {
      const serverUrl = this.serverInput.value || "ws://localhost:2567";
      try {
        await this.auth.fetchMe(serverUrl);
        // Apply max owned level to current loadout so player gets their upgrades
        const bp = this.vehicles[this.selectedIdx];
        if (bp) {
          for (const key of UPGRADE_KEYS) {
            const owned = this.auth.getOwnedLevel(bp.id, "performance", key) as 0 | 1 | 2 | 3;
            if ((this.currentUpgrades[key] as number) < owned) this.currentUpgrades[key] = owned;
          }
          const WEAPON_KEYS = ["damage", "reload", "velocity", "range", "streams"] as const;
          for (const key of WEAPON_KEYS) {
            const owned = this.auth.getOwnedLevel(bp.id, "weapon", key) as 0 | 1 | 2 | 3;
            if ((this.currentWeaponUpgrades[key] as number) < owned) this.currentWeaponUpgrades[key] = owned;
          }
        }
      } catch { /* use cached data */ }
    }
    this.renderCreditBalance();
    this.renderPresets();
    this.renderUpgradeControls();
    this.renderStatPreview();
    this.renderWeaponUpgradeControls();
    this.renderWeaponStatPreview();
  }

  private startGpTest(): void {
    if (this.gpTestPollId !== 0) return;
    this.gpTestActive = true;
    const statusEl  = document.getElementById("gamepad-test-status")!;
    const buttonsEl = document.getElementById("gamepad-test-buttons")!;
    const axesEl    = document.getElementById("gamepad-test-axes")!;

    const tick = (): void => {
      if (!this.gpTestActive) { this.gpTestPollId = 0; return; }
      const pads = navigator.getGamepads?.() ?? [];
      let pad: Gamepad | null = null;
      for (const p of pads) { if (p?.connected) { pad = p; break; } }

      if (!pad) {
        statusEl.textContent = "No controller detected — press any button";
        buttonsEl.classList.add("hidden");
        axesEl.classList.add("hidden");
      } else {
        statusEl.textContent = `🎮 ${pad.id.slice(0, 60)}`;
        const btnLabels = ["A","B","X","Y","LB","RB","LT","RT","SEL","STA","L3","R3"];
        buttonsEl.replaceChildren(...pad.buttons.map((b, i) => {
          const el = document.createElement("span");
          el.className = `gp-btn${b.pressed ? " gp-active" : ""}`;
          el.textContent = `${btnLabels[i] ?? i} ${b.value.toFixed(2)}`;
          return el;
        }));
        axesEl.replaceChildren(...pad.axes.map((v, i) => {
          const el = document.createElement("span");
          el.className = `gp-axis${Math.abs(v) > 0.1 ? " gp-active" : ""}`;
          el.textContent = `AX${i} ${v.toFixed(2)}`;
          return el;
        }));
        buttonsEl.classList.remove("hidden");
        axesEl.classList.remove("hidden");
      }
      this.gpTestPollId = requestAnimationFrame(tick);
    };
    this.gpTestPollId = requestAnimationFrame(tick);
  }

  private stopGpTest(): void {
    this.gpTestActive = false;
    if (this.gpTestPollId) { cancelAnimationFrame(this.gpTestPollId); this.gpTestPollId = 0; }
  }

  private renderActiveVehicleSummary(): void {
    const el = document.getElementById("active-vehicle-summary");
    if (!el || this.vehicles.length === 0) return;
    const bp = this.vehicles[this.selectedIdx];
    if (!bp) { el.innerHTML = ""; return; }
    el.innerHTML =
      `<div class="avs-row">` +
      `<span class="avs-dot" style="color:${bp.style.trimColor}">◉</span>` +
      `<span class="avs-name">${escapeHtml(bp.id)} — ${escapeHtml(bp.name)}</span>` +
      `<button class="avs-change-btn" id="avs-change-btn">CHANGE</button>` +
      `</div>`;
    document.getElementById("avs-change-btn")?.addEventListener("click", () => this.switchTab("vehicle"));
  }

  private loadVehicles(): void {
    for (const raw of RAW) {
      const result = validateVehicleBlueprint(raw);
      if (result.ok) {
        this.vehicles.push(result.blueprint);
      } else {
        const id = (raw as Record<string, unknown>)?.id ?? "unknown";
        console.error(`[VehicleSelect] blueprint "${id}" FAILED validation — vehicle will not appear:`, result.errors);
      }
    }
    if (this.vehicles.length === 0) {
      console.error("[VehicleSelect] no valid vehicles to choose from — check content/vehicles/*.json against LIMITS in gameConstants.ts");
    }
  }

  private refreshShowcase(): void {
    if (this.showcase) { this.showcase.destroy(); this.showcase = null; }
    const previewCanvas = document.getElementById("vehicle-preview-canvas") as HTMLCanvasElement | null;
    if (previewCanvas && this.vehicles.length > 0) {
      this.showcase = new VehicleShowcase(previewCanvas, this.vehicles, (idx) => {
        this.selectedIdx = idx;
        this.renderVehicleInfo();
        this.renderActiveVehicleSummary();
        this.vehiclesChanged?.();
      });
    }
    this.selectedIdx = 0;
    this.vehiclesChanged?.();
  }

  async loadCustomVehicles(): Promise<void> {
    if (!this.auth?.isLoggedIn) return;
    const serverUrl = wsToHttp(this.serverInput.value || "ws://localhost:2567");
    try {
      const res = await fetch(`${serverUrl}/api/vehicles/custom`, {
        headers: { Authorization: `Bearer ${this.auth.token!}` },
      });
      if (!res.ok) return;
      const data = await res.json() as { vehicles: unknown[] };
      const custom: VehicleBlueprint[] = [];
      for (const raw of data.vehicles) {
        const result = validateVehicleBlueprint(raw);
        if (result.ok) custom.push(result.blueprint);
      }
      // Rebuild: remove old custom entries, prepend fresh ones
      this.customVehicleIds = new Set(custom.map((v) => v.id));
      this.vehicles = [...custom, ...this.vehicles.filter((v) => !this.customVehicleIds.has(v.id) && !v.id.startsWith("cv-"))];
      this.refreshShowcase();
      if (this.activeTab === "vehicle" || this.garageActive) {
        this.showcase?.start();
        this.renderVehicleInfo();
      }
      this.renderActiveVehicleSummary();
    } catch { /* ignore network errors */ }
  }

  private async removeCustomVehicle(blueprintId: string): Promise<void> {
    if (!this.auth?.isLoggedIn) return;
    const serverUrl = wsToHttp(this.serverInput.value || "ws://localhost:2567");
    try {
      await fetch(`${serverUrl}/api/vehicles/custom/${encodeURIComponent(blueprintId)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${this.auth.token!}` },
      });
    } catch { /* ignore */ }
    // Remove locally regardless of server response
    this.customVehicleIds.delete(blueprintId);
    this.vehicles = this.vehicles.filter((v) => v.id !== blueprintId);
    if (this.selectedIdx >= this.vehicles.length) this.selectedIdx = Math.max(0, this.vehicles.length - 1);
    this.refreshShowcase();
    if (this.activeTab === "vehicle" || this.garageActive) { this.showcase?.start(); this.renderVehicleInfo(); }
    this.renderActiveVehicleSummary();
  }

  private wireVehicleNav(): void {
    document.getElementById("vehicle-prev-btn")?.addEventListener("click", () => {
      this.showcase?.prev();
    });
    document.getElementById("vehicle-next-btn")?.addEventListener("click", () => {
      this.showcase?.next();
    });
  }

  private renderVehicleInfo(): void {
    const bp = this.vehicles[this.selectedIdx];
    if (!bp) return;

    const nameEl = document.getElementById("showcase-vehicle-name");
    const subEl  = document.getElementById("showcase-vehicle-sub");
    const barsEl = document.getElementById("showcase-stat-bars");
    const dotsEl = document.getElementById("vehicle-dots");

    if (nameEl) {
      nameEl.textContent = bp.name;
      nameEl.style.color = bp.style.trimColor;
      (nameEl as HTMLElement).style.textShadow = `0 0 12px ${bp.style.trimColor}`;
    }
    if (subEl) subEl.textContent = bp.id.replace(/-/g, " ").toUpperCase();

    if (barsEl) {
      const MAX_SPEED    = 90;
      const MAX_HEALTH   = 200;
      const MAX_TURNRATE = 3.5;
      const MAX_TRACTION = 1.0;
      const MAX_DPS      = 35 / 200 * 1000; // max damage / min reloadMs * 1000
      const speed     = Math.round((bp.handling.maxSpeed / MAX_SPEED) * 100);
      const hull      = Math.round((bp.stats.maxHealth   / MAX_HEALTH) * 100);
      const agility   = Math.round(((bp.handling.turnRate / MAX_TURNRATE) * 0.55 +
                                    (bp.handling.traction / MAX_TRACTION) * 0.45) * 100);
      const dps       = bp.weapon.damage / bp.weapon.reloadMs * 1000;
      const firepower = Math.round(Math.min(dps / MAX_DPS, 1) * 100);

      const bars: Array<{ label: string; pct: number }> = [
        { label: "SPEED",     pct: speed },
        { label: "HULL",      pct: hull },
        { label: "AGILITY",   pct: agility },
        { label: "FIREPOWER", pct: firepower },
      ];
      barsEl.innerHTML = bars.map((b) =>
        `<div class="showcase-stat-row">` +
        `<span class="showcase-stat-label">${b.label}</span>` +
        `<div class="showcase-stat-track">` +
        `<div class="showcase-stat-fill" style="width:${b.pct}%;background:${bp.style.trimColor};box-shadow:0 0 6px ${bp.style.trimColor}"></div>` +
        `</div>` +
        `<span class="showcase-stat-val">${b.pct}</span>` +
        `</div>`
      ).join("");
    }

    if (dotsEl) {
      dotsEl.innerHTML = this.vehicles.map((_, i) =>
        `<button class="vehicle-dot${i === this.selectedIdx ? " active" : ""}" data-idx="${i}" aria-label="Vehicle ${i + 1}"></button>`
      ).join("");
      dotsEl.querySelectorAll<HTMLButtonElement>(".vehicle-dot").forEach((btn) => {
        btn.addEventListener("click", () => {
          const idx = parseInt(btn.dataset["idx"] ?? "0");
          this.showcase?.goTo(idx);
        });
      });
    }

    // Custom vehicle indicators
    const isCustom = this.customVehicleIds.has(bp.id);
    const badgeEl = document.getElementById("showcase-custom-badge");
    if (badgeEl) badgeEl.style.display = isCustom ? "inline-block" : "none";
    const editStyleBtn = document.getElementById("vehicle-edit-style-btn");
    if (editStyleBtn) editStyleBtn.classList.toggle("hidden", !isCustom);
    const deleteBtn = document.getElementById("vehicle-delete-btn");
    if (deleteBtn) deleteBtn.classList.toggle("hidden", !isCustom);
  }

  private async loadLeaderboard(): Promise<void> {
    if (!this.auth) return;
    const contentEl = document.getElementById("leaderboard-content");
    if (!contentEl) return;
    contentEl.innerHTML = `<div id="leaderboard-loading">Loading…</div>`;
    const serverUrl = this.serverInput.value || "ws://localhost:2567";
    try {
      const entries = await this.auth.fetchLeaderboard(serverUrl);
      this.renderLeaderboard(entries, contentEl);
    } catch {
      contentEl.innerHTML = `<div class="lb-error">Could not load leaderboard</div>`;
    }
  }

  private renderLeaderboard(entries: LeaderboardEntry[], container: HTMLElement): void {
    // Shared with the RANKINGS screen — logic moved to leaderboardView.ts (M3).
    container.innerHTML = buildLeaderboardContentHtml(entries);
  }

  vehicleCount(): number {
    return this.vehicles.length;
  }

  cycleSelection(delta: number): void {
    if (this.vehicles.length === 0) return;
    this.selectedIdx = (this.selectedIdx + delta + this.vehicles.length) % this.vehicles.length;
    this.showcase?.goTo(this.selectedIdx);
    this.renderVehicleInfo();
    this.renderActiveVehicleSummary();
  }

  /** The current lobby selection as a loadout. Pure read of this screen's state. */
  private buildResult(dummyOpponent: boolean): VehicleSelectResult | null {
    if (this.vehicles.length === 0) return null;
    const bp = this.vehicles[this.selectedIdx];
    const driver = (this.nameInput.value || "Driver").slice(0, 16);
    const server = this.serverInput.value || "ws://localhost:2567";
    const isGuest = !this.auth?.isLoggedIn;
    return {
      driverName: driver,
      serverUrl: server,
      blueprint: bp,
      keyboardOnly: this.controlMethod === "keyboard",
      controlMethod: this.controlMethod,
      upgrades: isGuest ? { ...DEFAULT_UPGRADES } : { ...this.currentUpgrades },
      weaponUpgrades: isGuest ? { ...DEFAULT_WEAPON_UPGRADES } : { ...this.currentWeaponUpgrades },
      dummyOpponent,
      gameMode: this.gameMode,
      preferredTeam: this.preferredTeam,
      renderMode: this.renderMode,
    };
  }

  confirm(dummyOpponent = false): void {
    const result = this.buildResult(dummyOpponent);
    if (!result) return;
    this.hide();
    this.opts.onConfirm(result);
  }

  confirmDummy(): void {
    this.confirm(true);
  }

  confirmQuickMatch(): void {
    if (!this.opts.onQuickMatch) return;
    const result = this.buildResult(false);
    if (!result) return;
    this.hide();
    this.opts.onQuickMatch(result);
  }

  show(): void {
    // Always reset join button state — no matter which path shows the lobby
    this.joinBtn.disabled = false;
    this.joinBtn.textContent = "DROP IN";
    (document.getElementById("dummy-btn") as HTMLButtonElement | null)?.removeAttribute("disabled");
    this.root.classList.remove("hidden");
    this.root.style.display = "flex";
    this.startGamepadWatcher();
  }
  hide(): void {
    this.root.style.display = "none";
    this.root.classList.add("hidden");
  }
  isVisible(): boolean {
    return this.root.style.display !== "none";
  }
  selectedBlueprint(): VehicleBlueprint | null {
    return this.vehicles[this.selectedIdx] ?? null;
  }

  // ── M3 facade: GARAGE / OPTIONS / RANKINGS screens drive the existing ─────
  // logic through these instead of duplicating it. None of them are called
  // in the classic (?ui=classic) flow.

  /** Where a guest lands when pressing a sign-in-gated control (shell: OPTIONS → ACCOUNT). */
  setRequireSignIn(fn: (() => void) | null): void {
    this.requireSignIn = fn ?? (() => this.switchTab("account"));
  }

  /** GARAGE list sync — fires on roster rebuilds and selection changes. */
  setVehiclesChangedHook(fn: (() => void) | null): void {
    this.vehiclesChanged = fn;
  }

  /** GARAGE screen entered: run showcase + vehicle info + upgrade panels together. */
  garageEnter(): void {
    this.garageActive = true;
    this.renderVehicleInfo();
    this.showcase?.start();
    void this.openUpgradesTab();
  }

  /** GARAGE screen left: park the showcase rAF loop. */
  garageLeave(): void {
    this.garageActive = false;
    this.showcase?.stop();
  }

  /** OPTIONS → CONTROLS section shown: live pad test + join-watcher keep running. */
  controlsEnter(): void {
    this.embeddedActive = true;
    this.startGpTest();
    this.startGamepadWatcher();
  }

  /** OPTIONS → CONTROLS section hidden: stop both loops. */
  controlsLeave(): void {
    this.embeddedActive = false;
    this.stopGpTest();
  }

  /** Roster snapshot for the GARAGE vehicle list. */
  vehicleListInfo(): Array<{ id: string; name: string; trim: string; custom: boolean; selected: boolean }> {
    return this.vehicles.map((bp, i) => ({
      id: bp.id,
      name: bp.name,
      trim: bp.style.trimColor,
      custom: this.customVehicleIds.has(bp.id),
      selected: i === this.selectedIdx,
    }));
  }

  /** GARAGE list click/confirm — routes through the showcase carousel. */
  selectVehicleAt(idx: number): void {
    this.showcase?.goTo(idx);
  }

  private renderPresets(): void {
    this.presetSelector.innerHTML = "";
    for (const key of Object.keys(CAR_PRESETS)) {
      const btn = document.createElement("button");
      btn.className = "preset-btn" + (key === this.selectedPreset ? " selected" : "");
      btn.textContent = key.toUpperCase();
      btn.addEventListener("click", () => {
        this.selectedPreset = key;
        this.currentUpgrades = { ...CAR_PRESETS[key] };
        this.renderPresets();
        this.renderUpgradeControls();
        this.renderStatPreview();
      });
      this.presetSelector.appendChild(btn);
    }
  }

  private renderCreditBalance(): void {
    const el = document.getElementById("credit-balance");
    if (!el) return;
    if (!this.auth?.isLoggedIn) {
      el.textContent = "GUEST — UPGRADES LOCKED";
      el.className = "credit-balance guest";
    } else {
      el.textContent = `✦ ${this.auth.getCredits()} CREDITS`;
      el.className = "credit-balance";
    }
  }

  private ownedCarLevel(key: keyof CarUpgrade): number {
    if (!this.auth?.isLoggedIn) return 0;
    const bp = this.vehicles[this.selectedIdx];
    if (!bp) return 0;
    return this.auth.getOwnedLevel(bp.id, "performance", key);
  }

  private renderUpgradeControls(): void {
    const el = document.getElementById("upgrade-controls");
    if (!el) return;
    const isGuest = !this.auth?.isLoggedIn;
    el.innerHTML = UPGRADE_KEYS.map((key) => {
      const val = this.currentUpgrades[key];
      const owned = this.ownedCarLevel(key);
      const canIncrease = !isGuest && val < owned;
      const canBuy = !isGuest && val === owned && owned < 3 && (this.auth?.getCredits() ?? 0) >= 1;
      const canDecrease = !isGuest && val > 0;
      return `<div class="uc-row">
        <span class="uc-label">${key.toUpperCase()}</span>
        <button class="uc-step-btn" data-key="${key}" data-delta="-1"${!canDecrease ? " disabled" : ""}>−</button>
        <span class="uc-level">${val}${owned > 0 ? `<span class="uc-owned">/${owned}</span>` : ""}</span>
        ${canBuy
          ? `<button class="uc-step-btn uc-buy-btn" data-key="${key}" data-slot="performance">BUY</button>`
          : `<button class="uc-step-btn" data-key="${key}" data-delta="1"${!canIncrease ? " disabled" : ""}>+</button>`
        }
      </div>`;
    }).join("");

    el.querySelectorAll<HTMLButtonElement>(".uc-step-btn:not(.uc-buy-btn)").forEach((btn) => {
      btn.addEventListener("click", () => {
        const key = btn.dataset["key"] as keyof CarUpgrade;
        const delta = parseInt(btn.dataset["delta"] ?? "0");
        const owned = this.ownedCarLevel(key);
        const next = Math.max(0, Math.min(owned, this.currentUpgrades[key] + delta)) as 0 | 1 | 2 | 3;
        this.currentUpgrades[key] = next;
        if (!matchesPreset(this.currentUpgrades, this.selectedPreset)) {
          this.selectedPreset = "";
          this.renderPresets();
        }
        this.renderUpgradeControls();
        this.renderStatPreview();
      });
    });

    el.querySelectorAll<HTMLButtonElement>(".uc-buy-btn").forEach((btn) => {
      btn.addEventListener("click", async () => {
        if (!this.auth) return;
        const bp = this.vehicles[this.selectedIdx];
        if (!bp) return;
        const key = btn.dataset["key"] as keyof CarUpgrade;
        const targetLevel = (this.ownedCarLevel(key) + 1) as 1 | 2 | 3;
        const serverUrl = this.serverInput.value || "ws://localhost:2567";
        const result = await this.auth.purchaseUpgrade(serverUrl, bp.id, "performance", key, targetLevel);
        if (result.ok) {
          this.currentUpgrades[key] = targetLevel;
          if (!matchesPreset(this.currentUpgrades, this.selectedPreset)) {
            this.selectedPreset = "";
            this.renderPresets();
          }
        }
        this.renderCreditBalance();
        this.renderUpgradeControls();
        this.renderStatPreview();
      });
    });
  }

  private renderStatPreview(): void {
    const bp = this.vehicles[this.selectedIdx];
    if (!bp) { this.presetStatPreview.innerHTML = ""; return; }

    const stats = computeStats(bp, this.currentUpgrades);

    const MAX: Record<string, number> = {
      topSpeed: 50, accel: 30, brakeForce: 35, turnRate: 5,
      traction: 1, drift: 1, mass: 20, boostPower: 2,
      downforce: 1, gearShiftSpeed: 2, armour: 10, maxHealth: 200,
    };

    const rows: Array<{ label: string; value: number; max: number; dim?: boolean }> = [
      { label: "TOP SPEED",  value: stats.topSpeed,       max: MAX.topSpeed },
      { label: "ACCEL",      value: stats.accel,          max: MAX.accel },
      { label: "BRAKE",      value: stats.brakeForce,     max: MAX.brakeForce },
      { label: "TURN RATE",  value: stats.turnRate,       max: MAX.turnRate },
      { label: "TRACTION",   value: stats.traction,       max: MAX.traction },
      { label: "DRIFT",      value: stats.drift,          max: MAX.drift },
      { label: "MASS",       value: stats.mass,           max: MAX.mass },
      { label: "BOOST PWR",  value: stats.boostPower,     max: MAX.boostPower },
      { label: "DOWNFORCE",  value: stats.downforce,      max: MAX.downforce },
      { label: "GEAR SPD",   value: stats.gearShiftSpeed, max: MAX.gearShiftSpeed },
      { label: "ARMOUR",     value: bp.stats.armour,      max: MAX.armour, dim: true },
      { label: "HULL",       value: bp.stats.maxHealth,   max: MAX.maxHealth, dim: true },
    ];

    const rowsHtml = rows.map((r) => {
      const pct = Math.round(Math.max(0, Math.min(1, r.value / r.max)) * 100);
      const display = Number.isInteger(r.value) ? String(r.value) : r.value.toFixed(2);
      const dimClass = r.dim ? " ps-dim" : "";
      return `<div class="ps-row${dimClass}">` +
        `<span class="ps-label">${r.label}</span>` +
        `<span class="ps-bar"><span class="ps-bar-fill" style="width:${pct}%"></span></span>` +
        `<span class="ps-value">${display}</span>` +
        `</div>`;
    }).join("");

    const titleLabel = this.selectedPreset ? this.selectedPreset.toUpperCase() : "CUSTOM";
    this.presetStatPreview.innerHTML =
      `<div class="ps-title">STATS — ${escapeHtml(titleLabel)}</div>` +
      rowsHtml;
  }
  private ownedWeaponLevel(key: keyof WeaponUpgrade): number {
    if (!this.auth?.isLoggedIn) return 0;
    const bp = this.vehicles[this.selectedIdx];
    if (!bp) return 0;
    return this.auth.getOwnedLevel(bp.id, "weapon", key);
  }

  private renderWeaponUpgradeControls(): void {
    const el = document.getElementById("weapon-upgrade-controls");
    if (!el) return;
    const WEAPON_KEYS: Array<keyof WeaponUpgrade> = ["damage", "reload", "velocity", "range", "streams"];
    const isGuest = !this.auth?.isLoggedIn;
    el.innerHTML = WEAPON_KEYS.map((key) => {
      const val = this.currentWeaponUpgrades[key] ?? 0;
      const owned = this.ownedWeaponLevel(key);
      // Streams: max purchasable level is 2 (L1→2 parallel, L2→5 fan)
      const maxBuyLevel = key === "streams" ? 2 : 3;
      const canIncrease = !isGuest && val < owned;
      const canBuy = !isGuest && val === owned && owned < maxBuyLevel && (this.auth?.getCredits() ?? 0) >= 1;
      const canSell = !isGuest && val > 0 && val === owned;
      const canDeactivate = !isGuest && val > 0 && val < owned;
      const decreaseBtn = canSell
        ? `<button class="uc-step-btn uc-sell-btn" data-wkey="${key}" data-slot="weapon" title="Sell — refunds 1 credit">SELL</button>`
        : `<button class="uc-step-btn" data-wkey="${key}" data-wdelta="-1"${!canDeactivate ? " disabled" : ""}>−</button>`;
      // For streams, display actual stream count (1 / 3 / 5) not upgrade level (0 / 1 / 2)
      const STREAM_COUNTS = [1, 2, 5] as const;
      const displayVal   = key === "streams" ? STREAM_COUNTS[Math.min(2, val)]   : val;
      const displayOwned = key === "streams" ? STREAM_COUNTS[Math.min(2, owned)] : owned;
      return `<div class="uc-row">
        <span class="uc-label">${key.toUpperCase()}</span>
        ${decreaseBtn}
        <span class="uc-level">${displayVal}${owned > 0 ? `<span class="uc-owned">/${displayOwned}</span>` : ""}</span>
        ${canBuy
          ? `<button class="uc-step-btn uc-buy-btn" data-wkey="${key}" data-slot="weapon">BUY</button>`
          : `<button class="uc-step-btn" data-wkey="${key}" data-wdelta="1"${!canIncrease ? " disabled" : ""}>+</button>`
        }
      </div>`;
    }).join("");

    el.querySelectorAll<HTMLButtonElement>(".uc-step-btn:not(.uc-buy-btn):not(.uc-sell-btn)[data-wkey]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const key = btn.dataset["wkey"] as keyof WeaponUpgrade;
        const delta = parseInt(btn.dataset["wdelta"] ?? "0");
        const owned = this.ownedWeaponLevel(key);
        const next = Math.max(0, Math.min(owned, (this.currentWeaponUpgrades[key] ?? 0) + delta)) as 0 | 1 | 2 | 3;
        this.currentWeaponUpgrades[key] = next;
        this.renderWeaponUpgradeControls();
        this.renderWeaponStatPreview();
      });
    });

    el.querySelectorAll<HTMLButtonElement>(".uc-buy-btn[data-wkey]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        if (!this.auth) return;
        const bp = this.vehicles[this.selectedIdx];
        if (!bp) return;
        const key = btn.dataset["wkey"] as keyof WeaponUpgrade;
        const targetLevel = (this.ownedWeaponLevel(key) + 1) as 1 | 2 | 3;
        const serverUrl = this.serverInput.value || "ws://localhost:2567";
        const result = await this.auth.purchaseUpgrade(serverUrl, bp.id, "weapon", key, targetLevel);
        if (result.ok) {
          this.currentWeaponUpgrades[key] = targetLevel;
        }
        this.renderCreditBalance();
        this.renderWeaponUpgradeControls();
        this.renderWeaponStatPreview();
      });
    });

    el.querySelectorAll<HTMLButtonElement>(".uc-sell-btn[data-wkey]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        if (!this.auth) return;
        const bp = this.vehicles[this.selectedIdx];
        if (!bp) return;
        const key = btn.dataset["wkey"] as keyof WeaponUpgrade;
        const serverUrl = this.serverInput.value || "ws://localhost:2567";
        const result = await this.auth.sellUpgrade(serverUrl, bp.id, "weapon", key);
        if (result.ok) {
          const newOwned = this.ownedWeaponLevel(key);
          const clamped = Math.min(this.currentWeaponUpgrades[key] ?? 0, newOwned) as 0 | 1 | 2 | 3;
          this.currentWeaponUpgrades[key] = clamped;
        }
        this.renderCreditBalance();
        this.renderWeaponUpgradeControls();
        this.renderWeaponStatPreview();
      });
    });
  }

  private renderWeaponStatPreview(): void {
    const el = document.getElementById("weapon-stat-preview");
    if (!el) return;
    const bp = this.vehicles[this.selectedIdx];
    if (!bp) { el.innerHTML = ""; return; }

    const ws = computeWeaponStats(bp, this.currentWeaponUpgrades);
    const baseRange = bp.weapon.range;

    const rows: Array<{ label: string; value: number; max: number }> = [
      { label: "DAMAGE",   value: ws.damage,           max: bp.weapon.damage * 1.65 },
      { label: "RELOAD",   value: ws.reloadMs / 1000,  max: bp.weapon.reloadMs / 1000 },
      { label: "VELOCITY", value: ws.projectileSpeed,  max: bp.weapon.projectileSpeed * 1.50 },
      { label: "RANGE",    value: ws.range,             max: baseRange * 1.55 },
    ];

    const rowsHtml = rows.map((r) => {
      const pct = Math.round(Math.max(0, Math.min(1, r.value / r.max)) * 100);
      const display = Number.isInteger(r.value) ? String(r.value) : r.value.toFixed(2);
      return `<div class="ps-row">` +
        `<span class="ps-label">${r.label}</span>` +
        `<span class="ps-bar"><span class="ps-bar-fill" style="width:${pct}%"></span></span>` +
        `<span class="ps-value">${display}</span>` +
        `</div>`;
    }).join("");

    const streamsDisplay = ws.streams === 1 ? "1× single" : ws.streams === 2 ? "2× dual" : "5× fan";
    const streamsRow = `<div class="ps-row">` +
      `<span class="ps-label">STREAMS</span>` +
      `<span class="ps-bar"><span class="ps-bar-fill" style="width:${Math.round((ws.streams / 3) * 100)}%"></span></span>` +
      `<span class="ps-value">${streamsDisplay}</span>` +
      `</div>`;

    const w2 = bp.weapon2;
    const weapon2Html = w2 ? `<div class="ps-title" style="margin-top:8px">SECONDARY — ${w2.type.toUpperCase()}</div>` +
      `<div class="ps-row"><span class="ps-label">DAMAGE</span><span class="ps-value">${w2.damage}</span></div>` +
      `<div class="ps-row"><span class="ps-label">RELOAD</span><span class="ps-value">${(w2.reloadMs / 1000).toFixed(1)}s</span></div>` +
      `<div class="ps-row"><span class="ps-label">BIND</span><span class="ps-value">Q / L-BUMPER</span></div>` : "";

    el.innerHTML = `<div class="ps-title">WEAPON STATS</div>` + rowsHtml + streamsRow + weapon2Html;
  }
}

function wsToHttp(url: string): string {
  return url.replace(/^wss?:\/\//, (m) => (m === "wss://" ? "https://" : "http://"));
}

function matchesPreset(u: CarUpgrade, key: string): boolean {
  const p = CAR_PRESETS[key];
  if (!p) return false;
  return (Object.keys(u) as Array<keyof CarUpgrade>).every((k) => u[k] === p[k]);
}

function statBar(label: string, value: number, limits: { min: number; max: number }, color: string): string {
  const pct = Math.round(Math.max(0, Math.min(1, (value - limits.min) / (limits.max - limits.min))) * 100);
  const display = Number.isInteger(value) ? String(value) : value.toFixed(2);
  return `<div class="stat-bar-row">
    <span class="stat-bar-label">${label}</span>
    <span class="stat-bar-track"><span class="stat-bar-fill" style="width:${pct}%;background:${color}"></span></span>
    <span class="stat-bar-value">${display}</span>
  </div>`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === '"' ? "&quot;" : "&#39;",
  );
}
